import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { datadogRum } from '@datadog/browser-rum';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import type { Activity } from 'models/Activity';
import { BiologicalSex } from 'models/User';

import { useAuthentication } from 'hooks/useAuthentication';
import { useIsWidget } from 'hooks/useIsWidget';
import { usePartnerId } from 'hooks/usePartnerId';
import { useStorage } from 'hooks/useStorage';

import { validateUUID } from 'utils/validateUUID';

import type { Price } from 'templates/Schedule/shared/types/price';
import { mountPickDateUrl } from 'templates/Schedule/shared/utils/mountPickDateUrl';
import { scheduleSteps } from 'templates/Schedule/shared/utils/scheduleSteps';

interface ClearStateProps {
  persistSubproduct?: boolean;
}

export interface AppointmentInfoProps {
  productId: string;
  productSlug: string;
  startDate?: Date;
  doctorId?: string;
  doctorPrice?: Price;
  appointmentTime?: AppointmentTimeProps;
  searchId?: string;
  quantity: number;
  reason: string;
  patient: PatientProps;
  address?: string;
  clearState: (params?: ClearStateProps) => void;
  selectedAvailabilitySlot: string;
  /**
   * TODO: Include props such as price to the productInfo as an object.
   */
  selectedSubproduct: Activity | null;
}

export interface StepsProps {
  currentIndex: number;
  previousRoute: string;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  setPreviousRoute: React.Dispatch<React.SetStateAction<string>>;
}

interface PatientProps {
  name?: string;
  surname?: string;
  birthdate?: string;
  description?: string;
  biologicalSex?: BiologicalSex;
}

interface AppointmentTimeProps {
  startTime: string;
  endTime: string;
}
interface ScheduleContextCtx {
  // sessionId represents the user symptom checker session.
  sessionId?: string;
  setSessionId?: React.Dispatch<React.SetStateAction<string>>;
  selectedDay?: Date;
  productId: string;
  steps: StepsProps;
  AppointmentInfo: AppointmentInfoProps;
  setSelectedDay: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setSelectedAvailabilitySlot: React.Dispatch<React.SetStateAction<string>>;
  setSelectedPatient: React.Dispatch<React.SetStateAction<PatientProps>>;
  setAppointmentReason: React.Dispatch<React.SetStateAction<string>>;
  setPatientAddress: React.Dispatch<React.SetStateAction<string>>;
  fillProductInfo: (productId: string, slug: string, quantity?: number) => void;
  fillPickDateInfo: (day: Date) => void;
  fillSelectedPatient: (patient?: Partial<PatientProps>) => void;
  fillPickSlotInfo: (
    doctorId: string,
    appointmentTime: AppointmentTimeProps,
    searchId: string,
    availabilitySelected: string,
    doctorPrice?: Price,
  ) => void;
  setSelectedSubproduct: React.Dispatch<React.SetStateAction<Activity | null>>;
}

const ScheduleContext = createContext({} as ScheduleContextCtx);

export const ScheduleProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(
    scheduleSteps.pickDate.index,
  );

  const [previousStepRoute, setPreviousStepRoute] = useState('/');

  const [productQuantity, setProductQuantity] = useState(1);

  const [selectedProductId, setSelectedProductId] = useState('');

  const [selectedProductSlug, setSelectedProductSlug] = useState('');

  const [availabilitySearchId, setAvailabilitySearchId] = useState('');

  const [selectedAvailabilitySlot, setSelectedAvailabilitySlot] = useState('');

  const [selectedSubproduct, setSelectedSubproduct] = useState<Activity | null>(
    null,
  );

  const [selectedDay, setSelectedDay] = useState<Date>();

  const [selectedDoctorId, setSelectedDoctorId] = useState('');

  const [selectedDoctorPrice, setSelectedDoctorPrice] = useState<Price>(null);

  const [selectedTime, setSelectedTime] = useState<AppointmentTimeProps>(
    {} as AppointmentTimeProps,
  );

  const [selectedPatient, setSelectedPatient] = useState<PatientProps>(
    {} as PatientProps,
  );

  const [appointmentReason, setAppointmentReason] = useState('');

  const [sessionId, setSessionId] = useState('');

  const [patientAddress, setPatientAddress] = useState('');

  const router = useRouter();
  const { set, get, remove } = useStorage();

  const { tokenIsValid, userData } = useAuthentication();
  const { isWidget, isWidgetFirstRender } = useIsWidget();

  const { partnerId } = usePartnerId();

  const fillProductInfo = (productId: string, slug: string, quantity = 1) => {
    if (!productId || !slug) {
      datadogRum.addAction('useSchedule/fillProductInfo/redirectToHome', {
        productId,
        slug,
        quantity,
      });

      router.push('/');
    }

    setSelectedProductId(productId);

    setSelectedProductSlug(slug);

    setProductQuantity(quantity);
  };

  const fillPickDateInfo = (day: Date) => {
    setSelectedDay(day);
  };

  const fillSelectedPatient = (patient?: Partial<PatientProps>) => {
    const user = userData();

    setSelectedPatient({
      name: user?.name,
      surname: user?.surname,
      birthdate: dayjs(user?.birthdate).format('DD/MM/YYYY'),
      description: '',
      ...patient,
    });
  };

  const fillPickSlotInfo = (
    doctorId: string,
    appointmentTime: AppointmentTimeProps,
    searchId: string,
    availabilitySelected: string,
    doctorPrice?: Price,
  ) => {
    setSelectedDoctorId(doctorId);

    setSelectedDoctorPrice(doctorPrice);

    setSelectedTime(appointmentTime);

    setAvailabilitySearchId(searchId);

    setSelectedAvailabilitySlot(availabilitySelected);
  };

  const clearState = useCallback(
    ({ persistSubproduct = false }: ClearStateProps = {}) => {
      setCurrentStepIndex(1);

      setPreviousStepRoute('/');

      setProductQuantity(1);

      setSelectedDay(undefined);

      setSelectedProductId('');

      setAvailabilitySearchId('');

      setAppointmentReason('');

      setSelectedAvailabilitySlot('');

      if (!persistSubproduct) {
        setSelectedSubproduct(null);
      }

      remove('state');
    },
    [remove],
  );

  const AppointmentInfo = {
    productId: selectedProductId,
    productSlug: selectedProductSlug,
    quantity: productQuantity,
    startDate: selectedDay,
    doctorId: selectedDoctorId,
    doctorPrice: selectedDoctorPrice,
    appointmentTime: selectedTime,
    patient: selectedPatient,
    reason: appointmentReason,
    searchId: availabilitySearchId,
    address: patientAddress,
    selectedAvailabilitySlot,
    selectedSubproduct: selectedSubproduct,
    clearState,
  };

  const steps = {
    currentIndex: currentStepIndex,
    previousRoute: previousStepRoute,
    setCurrentIndex: setCurrentStepIndex,
    setPreviousRoute: setPreviousStepRoute,
  };

  // TODO: find a way to wrap only the schedule flow pages with context so we don't have to make this check
  const validateData = useCallback(() => {
    if (!tokenIsValid() || !router.isReady) {
      return;
    }

    const scheduleRoutes = Object.values(scheduleSteps)
      .map((step) => step.path)
      .filter((path) => path.includes('/schedule/'));

    if (!scheduleRoutes.some((element) => router.route.includes(element))) {
      return;
    }

    const state = JSON.parse(get('state'));

    setSelectedProductId(state?.selectedProductId);

    const isPickSlot = router.asPath === scheduleSteps.pickSlot.path;
    const isInvalidDate = dayjs(state?.selectedDay)
      .startOf('day')
      .isBefore(dayjs().startOf('day'));

    if (isPickSlot || isInvalidDate) {
      clearState();

      router.push(
        mountPickDateUrl(
          {
            productId: state?.selectedProductId,
            productSlug: state?.selectedProductSlug,
            quantity: state?.productQuantity,
          } as AppointmentInfoProps,
          partnerId,
        ),
      );
    }
  }, [clearState, get, router, tokenIsValid, partnerId]);

  useEffect(() => {
    validateData();

    function updateStates() {
      const state = JSON.parse(get('state'));

      state?.selectedDay && setSelectedDay(new Date(state.selectedDay));

      state?.currentStepIndex &&
        setCurrentStepIndex(Number(state.currentStepIndex));

      state?.previousStepRoute && setPreviousStepRoute(state.previousStepRoute);

      state?.productQuantity &&
        setProductQuantity(Number(state.productQuantity));

      state?.selectedProductId && setSelectedProductId(state.selectedProductId);

      state?.selectedProductSlug &&
        setSelectedProductSlug(state.selectedProductSlug);

      state?.availabilitySearchId &&
        setAvailabilitySearchId(state.availabilitySearchId);

      state?.selectedDoctorId && setSelectedDoctorId(state.selectedDoctorId);

      state?.selectedDoctorPrice &&
        setSelectedDoctorPrice(state.selectedDoctorPrice);

      state?.selectedTime &&
        setSelectedTime(state.selectedTime as AppointmentTimeProps);

      state?.selectedPatient &&
        setSelectedPatient(state.selectedPatient as PatientProps);

      state?.patientAddress && setPatientAddress(state.patientAddress);

      state?.selectedAvailabilitySlot &&
        setSelectedAvailabilitySlot(state.selectedAvailabilitySlot);

      state?.appointmentReason && setAppointmentReason(state.appointmentReason);

      state?.selectedSubproduct &&
        setSelectedSubproduct(state.selectedSubproduct);
    }

    updateStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = {
      currentStepIndex,
      previousStepRoute,
      productQuantity,
      selectedProductId,
      selectedProductSlug,
      availabilitySearchId,
      selectedDay,
      selectedDoctorId,
      selectedDoctorPrice,
      selectedTime,
      selectedPatient,
      appointmentReason,
      patientAddress,
      selectedAvailabilitySlot,
      selectedSubproduct,
    };

    set('state', state);
  }, [
    appointmentReason,
    availabilitySearchId,
    currentStepIndex,
    previousStepRoute,
    productQuantity,
    selectedDay,
    selectedDoctorId,
    selectedDoctorPrice,
    selectedPatient,
    selectedProductId,
    selectedProductSlug,
    selectedTime,
    patientAddress,
    selectedAvailabilitySlot,
    selectedSubproduct,
    set,
  ]);

  useEffect(() => {
    if (!router.query?.sessionId) return;

    if (process.env.NEXT_PUBLIC_APP_ENV !== 'development') {
      if (!validateUUID(String(router.query.sessionId))) {
        console.error('Insert a valid UUID as sessionId.');

        return;
      }
    }

    setSessionId(String(router.query.sessionId));
  }, [isWidget, isWidgetFirstRender, router.query.sessionId]);

  return (
    <ScheduleContext.Provider
      value={{
        sessionId,
        setSessionId,
        selectedDay,
        productId: selectedProductId,
        steps,
        fillProductInfo,
        fillPickDateInfo,
        fillPickSlotInfo,
        fillSelectedPatient,
        setSelectedDay,
        setPatientAddress,
        setSelectedPatient,
        setAppointmentReason,
        setSelectedAvailabilitySlot,
        AppointmentInfo,
        setSelectedSubproduct,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = () => {
  const context = useContext(ScheduleContext);

  if (!context) {
    throw new Error('useSchedule hook must be used under ScheduleProvider');
  }

  return context;
};
